import axios from 'axios';
import {
  USER_LOGIN_REQUEST,
  USER_LOGIN_SUCCESS,
  USER_LOGIN_FAIL,
  USER_LOGOUT,
  MFA_SETUP_REQUEST,
  MFA_SETUP_SUCCESS,
  MFA_SETUP_FAIL,
  MFA_VERIFY_SETUP_REQUEST,
  MFA_VERIFY_SETUP_SUCCESS,
  MFA_VERIFY_SETUP_FAIL,
  MFA_VERIFY_REQUEST,
  MFA_VERIFY_SUCCESS,
  MFA_VERIFY_FAIL,
  MFA_STATUS_REQUEST,
  MFA_STATUS_SUCCESS,
  MFA_STATUS_FAIL,
  MFA_REGENERATE_REQUEST,
  MFA_REGENERATE_SUCCESS,
  MFA_REGENERATE_FAIL,
  MFA_CONFIRM_REGENERATE_REQUEST,
  MFA_CONFIRM_REGENERATE_SUCCESS,
  MFA_CONFIRM_REGENERATE_FAIL,
  MFA_DISABLE_REQUEST,
  MFA_DISABLE_SUCCESS,
  MFA_DISABLE_FAIL,
} from '../constants/userConstants';
// import { BASE_URL } from './ip';

const FRONTEND_AUTH_ERROR_CODES = new Set([1001, 1002, 1003]);
const HIDDEN_ALERT_IDS_STORAGE_KEY = 'home_hidden_alert_ids_v1';
const HIDDEN_ALERT_WATCHERS_STORAGE_KEY = 'home_hidden_alert_watchers_v1';
const NETWORK_HIDDEN_ALERT_KEYS_STORAGE_KEY = 'network_hidden_alert_keys_v1';
const NETWORK_ADVANCED_HIDE_RULES_STORAGE_KEY = 'network_advanced_hide_rules_v1';
const LAST_ACTIVITY_STORAGE_KEY = 'watcher_last_activity';

const hasAuthErrorCodePrefix = (message) => {
  if (typeof message !== 'string') return false;
  const match = message.trim().match(/^([0-9]+)\s*-\s+/);
  if (!match) return false;
  return FRONTEND_AUTH_ERROR_CODES.has(Number(match[1]));
};

const isAuthErrorResponse = (data) => {
  if (!data || typeof data !== 'object') return false;
  if (data.session_expired === true) return true;
  if (FRONTEND_AUTH_ERROR_CODES.has(Number(data.error_code))) return true;
  if (hasAuthErrorCodePrefix(data.msg)) return true;
  return false;
};

export const login = (formData) => async (dispatch) => {
  try {
    dispatch({ type: USER_LOGIN_REQUEST });

    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const { data } = await axios.post(`/api/login`, formData, config);

    // const data = {
    // 	session: {
    // 		_permanent: true,
    // 		id: 1,
    // 		loggedin: true,
    // 		role: 'admin',
    // 		username: 'watcheradmin'
    // 	},
    // 	status: true
    // };

    // Handle MFA responses
    if (data.status === 'mfa_setup_required' || data.status === 'mfa_required') {
      // Don't set localStorage yet - wait for MFA completion
      dispatch({
        type: USER_LOGIN_SUCCESS,
        payload: data,
      });
      return data;
    }

    if (data.status) {
      dispatch({
        type: USER_LOGIN_SUCCESS,
        payload: data,
      });

      localStorage.setItem('userInfo', JSON.stringify(data));
      localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
    } else {
      dispatch({
        type: USER_LOGIN_FAIL,
        payload: data,
      });
    }
    
    return data;
  } catch (err) {
    dispatch({
      type: USER_LOGIN_FAIL,
      payload:
        err.response && err.response.data.msg
          ? err.response.data.msg
          : err.message,
    });
  }
};

export const logout = () => async (dispatch) => {
  try {
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    await axios.get(`/api/logout`, config);
  } catch (err) {
    console.log('Logout request failed');
  } finally {
    localStorage.removeItem('userInfo');
    localStorage.removeItem(HIDDEN_ALERT_IDS_STORAGE_KEY);
    localStorage.removeItem(HIDDEN_ALERT_WATCHERS_STORAGE_KEY);
    localStorage.removeItem(NETWORK_HIDDEN_ALERT_KEYS_STORAGE_KEY);
    localStorage.removeItem(NETWORK_ADVANCED_HIDE_RULES_STORAGE_KEY);
    localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
    dispatch({ type: USER_LOGOUT });
  }
};

export const accountAction = (formData) => async (dispatch) => {
  try {
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const { data } = await axios.post(`/api/account`, formData, config);

    if (data && !data.status && isAuthErrorResponse(data)) {
      dispatch(logout());
    }

    return data;
  } catch (err) {
    if (err?.response && [401, 403].includes(err.response.status)) {
      dispatch(logout());
    }
    return err.response && err.response.data ? err.response.data : { status: false, msg: err.message };
  }
};

export const changeUsersData = (formData) => async (dispatch) => {
  try {
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const { data } = await axios.post(`/api/users`, formData, config);

    if (data && data.status) {
      return data;
    } else if (data && !data.status) {
      if (isAuthErrorResponse(data)) {
        dispatch(logout());
      }
      return data;
    } else {
      return {};
    }
  } catch (err) {
    if (err?.response && [401, 403].includes(err.response.status)) {
      dispatch(logout());
    }
    return err.response && err.response.data ? err.response.data : { status: false, msg: err.message };
  }
};

export const getUsersData = () => async (dispatch) => {
  try {
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const { data } = await axios.get(`/api/users`, config);

    // const data = {
    // 	userCols: [ 'Sl. No.', 'Username', 'Role', 'Action' ],
    // 	userRows: [ [ 'John', 'admin' ], [ 'Doe', 'user' ] ],
    // 	status: true
    // };

    if (data && data.status) {
      return data;
    } else if (data && !data.status) {
      if (isAuthErrorResponse(data)) {
        dispatch(logout());
      }
      return data;
    } else {
      return {};
    }
  } catch (err) {
    if (err?.response && [401, 403].includes(err.response.status)) {
      dispatch(logout());
    }
    return err.response && err.response.data ? err.response.data : {};
  }
};

export const getDeviceData = () => async (dispatch) => {
  try {
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const { data } = await axios.get(`/api/device`, config);

    // const data = {
    // 	BACOTStatus: 'Offline',
    // 	DNP3OTStatus: 'Offline',
    // 	MODOTStatus: 'Offline',
    // 	S7OTStatus: 'Online',
    // 	WTStatus: 'Online',
    // 	status: true
    // };

    if (data && data.status) {
      return data;
    } else if (data && !data.status) {
      if (isAuthErrorResponse(data)) {
        dispatch(logout());
      }
      return data;
    } else {
      return {};
    }
  } catch (err) {
    if (err?.response && [401, 403].includes(err.response.status)) {
      dispatch(logout());
    }
    return {};
  }
};

export const postDeviceData = (formData) => async (dispatch) => {
  try {
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const { data } = await axios.post(`/api/device`, formData, config);

    // console.log(formData);
    // const data = {
    // 	BACOTStatus: 'Offline',
    // 	DNP3OTStatus: 'Offline',
    // 	MODOTStatus: 'Offline',
    // 	S7OTStatus: 'Online',
    // 	WTStatus: 'Online',
    // 	status: true
    // };

    if (data && data.status) {
      return data;
    } else if (data && !data.status) {
      if (isAuthErrorResponse(data)) {
        dispatch(logout());
      }
      return data;
    } else {
      return {};
    }
  } catch (err) {
    if (err?.response && [401, 403].includes(err.response.status)) {
      dispatch(logout());
    }
    return {};
  }
};

// ==================== MFA Actions ====================

export const setupMFA = (tempSession) => async (dispatch) => {
  try {
    dispatch({ type: MFA_SETUP_REQUEST });

    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const { data } = await axios.get(`/api/mfa/setup?temp_session=${tempSession}`, config);

    if (data.status) {
      dispatch({
        type: MFA_SETUP_SUCCESS,
        payload: data,
      });
    } else {
      dispatch({
        type: MFA_SETUP_FAIL,
        payload: data.msg || 'Failed to setup MFA',
      });
    }

    return data;
  } catch (err) {
    dispatch({
      type: MFA_SETUP_FAIL,
      payload: err.response && err.response.data.msg ? err.response.data.msg : err.message,
    });
    return { status: false, msg: err.message };
  }
};

export const verifyMFASetup = (code, tempSession) => async (dispatch) => {
  try {
    dispatch({ type: MFA_VERIFY_SETUP_REQUEST });

    const formData = new FormData();
    formData.append('code', code);
    formData.append('temp_session', tempSession);

    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const { data } = await axios.post(`/api/mfa/verify-setup`, formData, config);

    if (data.status) {
      dispatch({
        type: MFA_VERIFY_SETUP_SUCCESS,
        payload: data,
      });

      // Store session in localStorage after successful MFA setup
      localStorage.setItem('userInfo', JSON.stringify(data));
      localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));

      // Complete the login: verify-setup returns the real authenticated session
      // (and sets the cookie). Without updating userLogin, the app still holds
      // the pre-MFA 'mfa_setup_required' status, so navigating to '/' bounces to
      // login → back to the setup page. Mirror verifyMFA.
      dispatch({
        type: USER_LOGIN_SUCCESS,
        payload: data,
      });
    } else {
      dispatch({
        type: MFA_VERIFY_SETUP_FAIL,
        payload: data.msg || 'Invalid verification code',
      });
    }

    return data;
  } catch (err) {
    dispatch({
      type: MFA_VERIFY_SETUP_FAIL,
      payload: err.response && err.response.data.msg ? err.response.data.msg : err.message,
    });
    return { status: false, msg: err.message };
  }
};

export const verifyMFA = (code, tempSession) => async (dispatch) => {
  try {
    dispatch({ type: MFA_VERIFY_REQUEST });

    const formData = new FormData();
    formData.append('code', code);
    formData.append('temp_session', tempSession);

    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const { data } = await axios.post(`/api/mfa/verify`, formData, config);

    if (data.status) {
      dispatch({
        type: MFA_VERIFY_SUCCESS,
        payload: data,
      });
      
      // Store session in localStorage after successful MFA verification
      localStorage.setItem('userInfo', JSON.stringify(data));
      localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
      
      // Also update the userLogin reducer to trigger navigation
      dispatch({
        type: USER_LOGIN_SUCCESS,
        payload: data,
      });
    } else {
      dispatch({
        type: MFA_VERIFY_FAIL,
        payload: data.msg || 'Invalid verification code',
      });
    }

    return data;
  } catch (err) {
    dispatch({
      type: MFA_VERIFY_FAIL,
      payload: err.response && err.response.data.msg ? err.response.data.msg : err.message,
    });
    return { status: false, msg: err.message };
  }
};

export const getMFAStatus = () => async (dispatch) => {
  try {
    dispatch({ type: MFA_STATUS_REQUEST });

    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const { data } = await axios.get(`/api/mfa/status`, config);

    if (data.status) {
      dispatch({
        type: MFA_STATUS_SUCCESS,
        payload: data.mfa_status,
      });
    } else {
      dispatch({
        type: MFA_STATUS_FAIL,
        payload: data.msg || 'Failed to get MFA status',
      });
    }

    return data;
  } catch (err) {
    dispatch({
      type: MFA_STATUS_FAIL,
      payload: err.response && err.response.data.msg ? err.response.data.msg : err.message,
    });
    return { status: false, msg: err.message };
  }
};

export const regenerateMFA = (currentPassword) => async (dispatch) => {
  try {
    dispatch({ type: MFA_REGENERATE_REQUEST });

    const formData = new FormData();
    formData.append('current_password', currentPassword);

    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const { data } = await axios.post(`/api/mfa/regenerate`, formData, config);

    if (data.status) {
      dispatch({
        type: MFA_REGENERATE_SUCCESS,
        payload: data,
      });
    } else {
      dispatch({
        type: MFA_REGENERATE_FAIL,
        payload: data.msg || 'Failed to regenerate MFA',
      });
    }

    return data;
  } catch (err) {
    dispatch({
      type: MFA_REGENERATE_FAIL,
      payload: err.response && err.response.data.msg ? err.response.data.msg : err.message,
    });
    return { status: false, msg: err.message };
  }
};

export const confirmRegenerateMFA = (code) => async (dispatch) => {
  try {
    dispatch({ type: MFA_CONFIRM_REGENERATE_REQUEST });

    const formData = new FormData();
    formData.append('code', code);

    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const { data } = await axios.post(`/api/mfa/confirm-regenerate`, formData, config);

    if (data.status) {
      dispatch({
        type: MFA_CONFIRM_REGENERATE_SUCCESS,
        payload: data,
      });
    } else {
      dispatch({
        type: MFA_CONFIRM_REGENERATE_FAIL,
        payload: data.msg || 'Failed to confirm MFA regeneration',
      });
    }

    return data;
  } catch (err) {
    dispatch({
      type: MFA_CONFIRM_REGENERATE_FAIL,
      payload: err.response && err.response.data.msg ? err.response.data.msg : err.message,
    });
    return { status: false, msg: err.message };
  }
};

export const disableMFA = (username) => async (dispatch) => {
  try {
    dispatch({ type: MFA_DISABLE_REQUEST });

    const formData = new FormData();
    formData.append('username', username);

    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const { data } = await axios.post(`/api/mfa/disable`, formData, config);

    if (data.status) {
      dispatch({
        type: MFA_DISABLE_SUCCESS,
        payload: data,
      });
    } else {
      dispatch({
        type: MFA_DISABLE_FAIL,
        payload: data.msg || 'Failed to disable MFA',
      });
    }

    return data;
  } catch (err) {
    dispatch({
      type: MFA_DISABLE_FAIL,
      payload: err.response && err.response.data.msg ? err.response.data.msg : err.message,
    });
    return { status: false, msg: err.message };
  }
};
